/**
 * DriversLane — Renders DRIVER_ON_VEHICLE graph edges as windows
 *
 * Lane 3: Each driver gets a deterministic colour.
 * Overlapping drivers are stacked within the lane.
 */
import { useMemo } from 'react';
import type { DriverOverlay, TimelineSelection, TimelineHover } from '../../../types/vehicleSceneTimeline';
import {
  timeWindowToRect,
  entityColor,
  formatTime,
  formatDuration,
  formatConfidence,
  toMs,
  type TimeScale,
} from '../../../utils/timelineUtils';

interface DriversLaneProps {
  drivers: DriverOverlay[];
  scale: TimeScale;
  selection: TimelineSelection | null;
  hover: TimelineHover | null;
  onSelect: (edgeId: string) => void;
  onHover: (h: TimelineHover | null) => void;
}

const ROW_HEIGHT = 28;
const ROW_GAP = 2;

function computeRows(drivers: DriverOverlay[]): Map<string, number> {
  const sorted = [...drivers].sort(
    (a, b) => new Date(a.from_ts).getTime() - new Date(b.from_ts).getTime(),
  );
  const rows = new Map<string, number>();
  const rowEnds: number[] = [];

  for (const drv of sorted) {
    const start = new Date(drv.from_ts).getTime();
    let placed = false;
    for (let r = 0; r < rowEnds.length; r++) {
      if (rowEnds[r] <= start) {
        rows.set(drv.edge_id, r);
        rowEnds[r] = new Date(drv.to_ts).getTime();
        placed = true;
        break;
      }
    }
    if (!placed) {
      rows.set(drv.edge_id, rowEnds.length);
      rowEnds.push(new Date(drv.to_ts).getTime());
    }
  }
  return rows;
}

export default function DriversLane({
  drivers,
  scale,
  selection,
  hover: _hover,
  onSelect,
  onHover,
}: DriversLaneProps) {
  const rows = useMemo(() => computeRows(drivers), [drivers]);
  const maxRow = useMemo(
    () => Math.max(0, ...Array.from(rows.values())),
    [rows],
  );
  const laneHeight = (maxRow + 1) * (ROW_HEIGHT + ROW_GAP) + 8;

  const isHighlighted = (edgeId: string): boolean => {
    if (!selection) return false;
    if (selection.type === 'driver' && selection.id === edgeId) return true;
    return selection.relatedDriverIds?.includes(edgeId) ?? false;
  };

  const isDimmed = (edgeId: string): boolean => {
    if (!selection) return false;
    return !isHighlighted(edgeId);
  };

  return (
    <div className="vst-lane" data-lane="drivers">
      <div className="vst-lane-label">
        <span className="vst-lane-dot" style={{ background: '#8b5cf6' }} />
        Drivers <span className="vst-lane-badge">graph</span>
      </div>
      <svg className="vst-lane-svg" width={scale.widthPx} height={laneHeight}>
        {drivers.map(drv => {
          const { x, width } = timeWindowToRect(drv.from_ts, drv.to_ts, scale);
          const color = entityColor(drv.from_entity_id);
          const row = rows.get(drv.edge_id) ?? 0;
          const y = 4 + row * (ROW_HEIGHT + ROW_GAP);
          const highlighted = isHighlighted(drv.edge_id);
          const dimmed = isDimmed(drv.edge_id);

          return (
            <g
              key={drv.edge_id}
              className="vst-lane-item"
              pointerEvents="auto"
              onClick={() => onSelect(drv.edge_id)}
              onMouseEnter={e =>
                onHover({
                  type: 'driver',
                  id: drv.edge_id,
                  mouseX: e.clientX,
                  mouseY: e.clientY,
                })
              }
              onMouseLeave={() => onHover(null)}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={x}
                y={y}
                width={width}
                height={ROW_HEIGHT}
                rx={4}
                fill={color}
                opacity={dimmed ? 0.2 : 0.8}
                stroke={highlighted ? '#fff' : color}
                strokeWidth={highlighted ? 2 : 1}
              />
              {width > 70 && (
                <text
                  x={x + 6}
                  y={y + ROW_HEIGHT / 2 + 1}
                  fill="#fff"
                  fontSize={10}
                  dominantBaseline="middle"
                  pointerEvents="none"
                >
                  {drv.source_driver_key || drv.from_entity_id}
                </text>
              )}
              <title>
                {`${drv.label || drv.from_entity_id}\nEdge: ${drv.edge_id}\n${formatTime(toMs(drv.from_ts))} – ${formatTime(toMs(drv.to_ts))} (${formatDuration(drv.from_ts, drv.to_ts)})\nConfidence: ${formatConfidence(drv.confidence)}\nDerivation: ${drv.derivation}\nAuthority: ${drv.authority_level}`}
              </title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
